import {
  addRow,
  createMapEdit,
  deleteNode,
  dropKind,
  linkNode,
  moveNode,
  editHint,
  tapCell,
  updateSelected,
} from "/src/features/stageEditor/mapEdit.ts";
import { editorIcon, kindIcon } from "/src/features/stageEditor/editorIcon.ts";
import { bindDrag } from "/mocks/js/stage-editor-drag.js";
import { bindCaseLoads, renderCaseLoads } from "/mocks/js/stage-editor-load.js";
import { applyRewards, renderRewardPools, snapshotRewards } from "/mocks/js/stage-editor-reward.js";
import { addCaseLoad } from "/src/features/stageEditor/caseLoad.ts";
import { appendArrow, prepareArrowSvg } from "/mocks/js/arrow-line.js";
import { defaultStartDeck, normalizeStartDeck } from "/src/features/stageEditor/startDeck.ts";
import { hideEditorTabs } from "/src/features/stageEditor/sheetChrome.ts";
import { validateMap } from "/src/features/stageEditor/mapValidate.ts";

const KINDS = [
  ["start", "開始"],
  ["case", "案件"],
  ["rest", "休日"],
  ["event", "はてな"],
  ["relic", "基盤"],
];

let state = createMapEdit();
let hover = null;
let wired = false;
let editor;
let grid;
let lines;
let palette;
let hint;
let sheet;
let preview;
let stretch;
let trash;

function nodes() {
  return Object.values(state.nodes);
}

function cellCenter(cell, wr) {
  const box = cell.getBoundingClientRect();
  return { x: box.left - wr.left + box.width / 2, y: box.top - wr.top + box.height / 2 };
}

function drawLines() {
  const wrap = document.getElementById("map-wrap");
  const wr = wrap.getBoundingClientRect();
  prepareArrowSvg(lines, wr.width, wr.height, "edge-arrow", "#3d4654");
  const cellOf = Object.fromEntries(
    [...grid.children].map((cell) => [`${cell.dataset.x},${cell.dataset.y}`, cell]),
  );
  for (const from of nodes()) {
    for (const toId of from.next) {
      const to = state.nodes[toId];
      const a = cellOf[`${from.x},${from.y}`];
      const b = to && cellOf[`${to.x},${to.y}`];
      if (!a || !b) continue;
      const start = cellCenter(a, wr);
      const end = cellCenter(b, wr);
      appendArrow(lines, "edge-arrow", start.x, start.y, end.x, end.y, 16);
    }
  }
}

function renderGrid() {
  grid.replaceChildren();
  grid.style.setProperty("--editor-rows", String(state.rows));
  for (let y = 0; y < state.rows; y += 1) {
    for (let x = 0; x < state.cols; x += 1) {
      const node = nodes().find((n) => n.x === x && n.y === y);
      const cell = document.createElement(node ? "div" : "button");
      if (!node) cell.type = "button";
      cell.className = "cell";
      cell.dataset.x = String(x);
      cell.dataset.y = String(y);
      const isHover = hover?.kind === "cell" && hover.x === x && hover.y === y;
      if (isHover) cell.classList.add("is-drop");
      if (node) {
        cell.classList.add("is-node");
        if (state.selectedId === node.id) cell.classList.add("is-sel");
        const rim = document.createElement("button");
        rim.type = "button";
        rim.className = "node-rim";
        rim.setAttribute("aria-label", `${node.kind}から辺を伸ばす`);
        const core = document.createElement("button");
        core.type = "button";
        core.className = "node-core";
        core.setAttribute("aria-label", `${node.kind} ${node.id}`);
        const img = document.createElement("img");
        img.src = editorIcon(node);
        img.alt = "";
        img.draggable = false;
        core.append(img);
        cell.append(rim, core);
        if (node.kind === "case") {
          const stats = document.createElement("span");
          stats.className = "cell-stats";
          stats.textContent = `${node.required}/${node.deadline}`;
          cell.append(stats);
        }
        bindDrag(rim, { nodeId: node.id, mode: "link" }, grid, trash, preview, setHover, onDrop, showPreview);
        bindDrag(core, { nodeId: node.id, mode: "move" }, grid, trash, preview, setHover, onDrop, showPreview, () => {
          state = tapCell(state, x, y);
          render();
        });
      } else {
        cell.classList.add("is-empty");
        cell.setAttribute("aria-label", `置けるマス ${x},${y}`);
        cell.textContent = "＋";
        cell.addEventListener("click", () => {
          state = tapCell(state, x, y);
          render();
        });
      }
      grid.appendChild(cell);
    }
  }
}

function showPreview(payload) {
  if (payload.mode === "link") return;
  const img = preview.querySelector("img");
  img.src = payload.kind ? kindIcon(payload.kind) : editorIcon(state.nodes[payload.nodeId]);
}

function hideStretch() {
  stretch.hidden = true;
  stretch.replaceChildren();
}

function drawStretch(fromId, clientX, clientY) {
  const node = state.nodes[fromId];
  const wrap = document.getElementById("map-wrap");
  const cell = [...grid.children].find((el) => Number(el.dataset.x) === node?.x && Number(el.dataset.y) === node?.y);
  if (!node || !cell) return hideStretch();
  const wr = wrap.getBoundingClientRect();
  const start = cellCenter(cell, wr);
  stretch.hidden = false;
  prepareArrowSvg(stretch, wr.width, wr.height, "stretch-arrow", "#d4a017");
  appendArrow(stretch, "stretch-arrow", start.x, start.y, clientX - wr.left, clientY - wr.top, 8);
}

function setHover(next, payload, clientX, clientY) {
  hover = next;
  trash.classList.toggle("is-drop", next?.kind === "trash" && payload?.mode !== "link");
  for (const el of grid.children) {
    const on = next?.kind === "cell" && Number(el.dataset.x) === next.x && Number(el.dataset.y) === next.y;
    el.classList.toggle("is-drop", Boolean(on));
  }
  if (payload?.mode === "link" && payload.nodeId) drawStretch(payload.nodeId, clientX, clientY);
  else hideStretch();
}

function onDrop(payload, target) {
  hover = null;
  hideStretch();
  if (!target) return render();
  if (target.kind === "trash" && payload.mode === "move" && payload.nodeId) {
    state = deleteNode(state, payload.nodeId);
    return render();
  }
  if (target.kind !== "cell") return render();
  const before = state.nextSeq;
  if (payload.kind) state = dropKind(state, payload.kind, target.x, target.y);
  if (payload.mode === "move" && payload.nodeId) state = moveNode(state, payload.nodeId, target.x, target.y);
  if (payload.mode === "link" && payload.nodeId) state = linkNode(state, payload.nodeId, target.x, target.y);
  render();
  if (payload.kind === "start" && state.nextSeq === before) {
    hint.textContent = "開始は1つまで。置く位置を変えるときは旗をドラッグ。";
  }
}

function renderPalette() {
  palette.replaceChildren();
  for (const [kind, label] of KINDS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "chip";
    btn.innerHTML = `<img src="${kindIcon(kind)}" alt="">${label}`;
    bindDrag(btn, { kind }, grid, trash, preview, setHover, onDrop, showPreview);
    palette.appendChild(btn);
  }
}

function bindField(id, key, numeric) {
  const el = document.getElementById(id);
  if (!el || el.dataset.bound) return;
  el.dataset.bound = "1";
  el.addEventListener("input", () => {
    state = updateSelected(state, { [key]: numeric ? Number(el.value) : el.value });
    renderGrid();
    drawLines();
    renderIssues();
  });
}

function renderSheet() {
  const node = state.selectedId ? state.nodes[state.selectedId] : null;
  sheet.hidden = node?.kind !== "case";
  editor.classList.toggle("is-editing-case", hideEditorTabs(node?.kind));
  if (node?.kind !== "case") return;
  document.getElementById("sheet-title").textContent = `案件 ${node.id}`;
  document.getElementById("case-fields").hidden = false;
  document.getElementById("case-name").value = node.name;
  document.getElementById("case-size").value = node.size;
  document.getElementById("req").value = String(node.required);
  document.getElementById("dl").value = String(node.deadline);
  document.getElementById("flavor").value = node.flavor;
  renderCaseLoads(node.loads, applyLoads);
}

function applyLoads(loads) {
  state = updateSelected(state, { loads });
  renderSheet();
}

function renderIssues() {
  const issues = validateMap(state);
  const el = document.getElementById("map-issues");
  el.hidden = issues.length === 0;
  el.textContent = issues.join(" / ");
  document.querySelectorAll(".editor-try").forEach((btn) => {
    btn.disabled = issues.length > 0;
  });
}

function render() {
  preview.hidden = true;
  preview.removeAttribute("style");
  hideStretch();
  hint.textContent = editHint(state);
  renderPalette();
  renderGrid();
  renderSheet();
  renderIssues();
  requestAnimationFrame(drawLines);
}

export function snapshotEditor() {
  return {
    map: state,
    name: document.getElementById("st-name")?.value ?? "",
    difficulty: document.getElementById("st-diff")?.value ?? "",
    flavor: document.getElementById("st-flavor")?.value ?? "",
    rewards: snapshotRewards(),
    startDeck: snapshotStartDeck(),
  };
}

function deckCount(key) {
  return Number(document.querySelector(`[data-deck="${key}"] [data-count]`)?.textContent ?? 0);
}

function snapshotStartDeck() {
  return normalizeStartDeck({
    intern: deckCount("intern"),
    idle: deckCount("idle"),
    hedge: deckCount("hedge"),
    allowBringIn: Boolean(document.getElementById("st-bring-in")?.checked),
  });
}

function applyStartDeck(raw) {
  const deck = normalizeStartDeck(raw ?? defaultStartDeck());
  for (const key of ["intern", "idle", "hedge"]) {
    const el = document.querySelector(`[data-deck="${key}"] [data-count]`);
    if (el) el.textContent = String(deck[key]);
  }
  const bring = document.getElementById("st-bring-in");
  if (bring) bring.checked = deck.allowBringIn;
}

export function bootStageEditor(map, meta) {
  editor = document.querySelector(".editor");
  grid = document.getElementById("grid");
  lines = document.getElementById("lines");
  palette = document.getElementById("palette");
  hint = document.getElementById("hint");
  sheet = document.getElementById("sheet");
  preview = document.getElementById("drag-preview");
  stretch = document.getElementById("stretch");
  trash = document.getElementById("trash");
  if (!grid) return;
  if (map) state = map;
  if (meta) {
    const name = document.getElementById("st-name");
    const diff = document.getElementById("st-diff");
    const flavor = document.getElementById("st-flavor");
    if (name) name.value = meta.name;
    if (diff) diff.value = meta.difficulty;
    if (flavor) flavor.value = meta.flavor;
    applyRewards(meta.rewards);
    applyStartDeck(meta.startDeck);
  }
  if (!wired) {
    wired = true;
    bindEditorOnce();
  }
  render();
}

function bindEditorOnce() {
bindField("case-name", "name", false);
bindField("case-size", "size", false);
bindField("req", "required", true);
bindField("dl", "deadline", true);
bindField("flavor", "flavor", false);
bindCaseLoads((id) => {
  const node = state.selectedId ? state.nodes[state.selectedId] : null;
  if (node) applyLoads(addCaseLoad(node.loads, id));
});
document.getElementById("sheet-close").addEventListener("click", () => {
  state = { ...state, selectedId: null };
  render();
});
document.querySelectorAll("[data-tab]").forEach((tab) => {
  tab.addEventListener("click", () => {
    document.querySelectorAll("[data-tab]").forEach((t) => t.classList.toggle("is-on", t === tab));
    document.querySelectorAll("[data-panel]").forEach((p) => {
      p.classList.toggle("is-on", p.dataset.panel === tab.dataset.tab);
    });
  });
});
document.getElementById("add-row").addEventListener("click", () => {
  state = addRow(state);
  render();
});
renderRewardPools();
document.querySelectorAll("[data-step]").forEach((row) => {
  const value = row.querySelector("[data-count]");
  row.querySelector("[data-minus]").addEventListener("click", () => {
    value.textContent = String(Math.max(0, Number(value.textContent) - 1));
  });
  row.querySelector("[data-plus]").addEventListener("click", () => {
    value.textContent = String(Math.min(20, Number(value.textContent) + 1));
  });
});

window.addEventListener("resize", drawLines);
}

if (document.getElementById("grid")) bootStageEditor();
